/**
 * 开户页键盘避让能力三层判定：
 *
 * 1. `viewport-lock` — iOS 15.5+，或 Android WebView/Chrome >= 139，且有 visualViewport
 * 2. `js-measure`    — Android < 139 / 老 iOS：纯 JS 测高 + scrollTop，测不到则半屏估算
 * 3. `none`          — 桌面 / PC 壳 / 非触屏：放弃
 *
 * Android 官方：Pre-M139 时 Layout/Visual Viewport 固定，IME 不会缩小 visualViewport；
 * M139 起 ime() inset 才独立缩 Visual Viewport。故 < 139（含 103）禁止挂 viewport-lock。
 */

export type KeyboardMode = 'viewport-lock' | 'js-measure' | 'none';

export type KeyboardOsKind = 'ios' | 'android' | 'desktop' | 'unknown';

export type KeyboardCapability = {
  mode: KeyboardMode;
  os: KeyboardOsKind;
  /** 主版本.次版本（iOS）或 Chrome 主版本（Android）；解析失败为 0 */
  version: number;
  hasVisualViewport: boolean;
};

/** viewport-lock：iOS Safari 15.5+ */
const IOS_VIEWPORT_LOCK_MIN = 15.5;

/** Android：M139 起 IME 才缩 visualViewport（官方 insets 文档） */
export const ANDROID_VIEWPORT_LOCK_MIN = 139;

const isPcShell = () =>
  typeof document !== 'undefined' &&
  !!document.getElementById('h5-shell')?.classList.contains('h5-shell--pc');

function parseIosVersion(ua: string): number {
  const m = ua.match(/OS (\d+)[_.](\d+)/) || ua.match(/Version\/(\d+)\.(\d+)/);
  if (!m) {
    const major = ua.match(/OS (\d+)/);
    return major ? +major[1] : 0;
  }
  return +m[1] + +m[2] / 100;
}

function parseAndroidChromeVersion(ua: string): number {
  const m = ua.match(/Chrome\/(\d+)/);
  return m ? +m[1] : 0;
}

export function parseKeyboardOs(ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''): {
  os: KeyboardOsKind;
  version: number;
} {
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return { os: 'ios', version: parseIosVersion(ua) };
  }
  if (
    typeof navigator !== 'undefined' &&
    navigator.platform === 'MacIntel' &&
    (navigator.maxTouchPoints ?? 0) > 1
  ) {
    return { os: 'ios', version: parseIosVersion(ua) || 13 };
  }
  if (/Android/i.test(ua)) {
    return { os: 'android', version: parseAndroidChromeVersion(ua) };
  }
  const touch =
    typeof window !== 'undefined' && ('ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0);
  if (!touch || /Windows NT|Macintosh|Linux x86_64|CrOS/i.test(ua)) {
    return { os: 'desktop', version: 0 };
  }
  return { os: 'unknown', version: 0 };
}

export function detectKeyboardCapability(): KeyboardCapability {
  if (typeof window === 'undefined') {
    return { mode: 'none', os: 'unknown', version: 0, hasVisualViewport: false };
  }

  const hasVisualViewport = !!window.visualViewport;
  const { os, version } = parseKeyboardOs();

  if (isPcShell() || os === 'desktop') {
    return { mode: 'none', os: os === 'desktop' ? 'desktop' : os, version, hasVisualViewport };
  }

  // 新库：仅 IME 会缩 vv 的环境
  if (hasVisualViewport) {
    if (os === 'ios' && version >= IOS_VIEWPORT_LOCK_MIN) {
      return { mode: 'viewport-lock', os, version, hasVisualViewport };
    }
    if (os === 'android' && version >= ANDROID_VIEWPORT_LOCK_MIN) {
      return { mode: 'viewport-lock', os, version, hasVisualViewport };
    }
  }

  // JS 测高 / 滚动（Android < 139 走这里）
  if (os === 'ios' || os === 'android') {
    return { mode: 'js-measure', os, version, hasVisualViewport };
  }
  if ('ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0) {
    return { mode: 'js-measure', os: 'unknown', version, hasVisualViewport };
  }

  return { mode: 'none', os, version, hasVisualViewport };
}

export const canUseViewportLockLib = () => detectKeyboardCapability().mode === 'viewport-lock';

export const canUseKeyboardAvoidJs = () => {
  const { mode } = detectKeyboardCapability();
  return mode === 'viewport-lock' || mode === 'js-measure';
};

/** Android < M139（或 UA 解析失败）：IME 不缩 visualViewport */
export function isAndroidWebViewKeyboardUnreliable(): boolean {
  const { os, version } = parseKeyboardOs();
  if (os !== 'android') return false;
  if (version <= 0) return true;
  return version < ANDROID_VIEWPORT_LOCK_MIN;
}
